import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Badge from '@mui/material/Badge';

// Icons
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import AddTaskIcon from '@mui/icons-material/AddTask';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import CloseIcon from '@mui/icons-material/Close';

import conversationUIStyles from '../styles/conversationUIStyles';
import SpeechToText from '../../SpeechToText';

const InputBar = forwardRef(({
  currentMode,
  therapistInputText,
  onInputChange,
  onSendMessage,
  isSending,
  sendError,
  canProposeManualGoal,
  onOpenManualGoalModal,
  canInitiateAssessment,
  onOpenAssessmentModal,
  isDisabled,
  placeholder,
  styles = conversationUIStyles,
  responsive = {}
}, ref) => {
  const [localInputText, setLocalInputText] = useState(therapistInputText || '');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Expose focus method to parent
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }));

  useEffect(() => {
    setLocalInputText(therapistInputText || '');
  }, [therapistInputText]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleLocalInputChange = (e) => {
    const value = e.target.value;
    setLocalInputText(value);
    
    // Debounce parent state update
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      if (onInputChange) {
        onInputChange({ target: { value } });
      }
    }, 300);
  };

  const handleSend = (e) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!isDisabled && localInputText.trim() && !isSending) {
      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Update parent state immediately
      if (onInputChange) {
        onInputChange({ target: { value: localInputText } });
      }
      
      // Send message
      setTimeout(() => {
        onSendMessage(e);
      }, 0);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const handleSpeechTranscript = (transcript) => {
    const newValue = localInputText ? `${localInputText} ${transcript}` : transcript;
    setLocalInputText(newValue);
    handleLocalInputChange({ target: { value: newValue } });
  };

  const getPlaceholderText = () => {
    if (placeholder) return placeholder;
    
    switch (currentMode) {
      case 'ai':
        return 'AI mode active - Switch to Human or Assisted mode to send messages';
      case 'assisted':
        return 'Type a message (AI will assist)...';
      case 'human':
        return 'Type a message...';
      default:
        return 'Type a message...';
    }
  };

  const getSendButtonStyle = () => {
    if (isDisabled || !localInputText.trim()) {
      return {
        backgroundColor: '#e2e8f0',
        color: '#94a3b8',
        cursor: 'not-allowed'
      };
    }
    
    return {
      ...styles.sendButton,
      ...styles.sendButtonMode[currentMode]
    };
  };

  const inputDisabled = isDisabled || currentMode === 'ai' || isSending;

  return (
    <Box sx={styles.inputBar}>
      {/* Action Buttons Group */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {canProposeManualGoal && (
          <Tooltip title="Propose Goal">
            <span>
              <IconButton
                onClick={onOpenManualGoalModal}
                disabled={inputDisabled}
                aria-label="Propose goal"
                sx={{
                  color: '#2196F3',
                  '&:hover': { backgroundColor: '#E3F2FD' },
                  '&.Mui-disabled': { color: '#e2e8f0' }
                }}
              >
                <AddTaskIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        
        {canInitiateAssessment && (
          <Tooltip title="Initiate Assessment">
            <span>
              <IconButton
                onClick={onOpenAssessmentModal}
                disabled={inputDisabled}
                aria-label="Initiate assessment"
                sx={{
                  color: '#9C27B0',
                  '&:hover': { backgroundColor: '#F3E5F5' },
                  '&.Mui-disabled': { color: '#e2e8f0' }
                }}
              >
                <AssessmentIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Input Field Container */}
      <Box
        sx={{
          ...styles.inputField,
          ...(isFocused && !inputDisabled && {
            backgroundColor: '#ffffff',
            borderColor: styles.sendButtonMode[currentMode].background 
              ? '#FF8C00' 
              : '#2196F3',
            boxShadow: `0 0 0 3px ${
              currentMode === 'ai' ? 'rgba(33,150,243,0.1)' :
              currentMode === 'human' ? 'rgba(76,175,80,0.1)' :
              'rgba(255,140,0,0.1)'
            }`
          }),
          opacity: inputDisabled ? 0.6 : 1
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={localInputText}
          onChange={handleLocalInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={getPlaceholderText()}
          disabled={inputDisabled}
          style={styles.textInput}
        />
        
        {/* Input Actions */}
        <Box sx={styles.inputActions}>
          {localInputText && !inputDisabled && (
            <IconButton
              size="small"
              onClick={() => {
                setLocalInputText('');
                handleLocalInputChange({ target: { value: '' } });
              }}
              aria-label="Clear input"
              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
          
          <SpeechToText
            onTranscript={handleSpeechTranscript}
            disabled={inputDisabled}
            continuous={false}
            showInterimText={false}
            customButton={(props) => (
              <IconButton
                {...props}
                size="small"
                aria-label={props.isListening ? 'Stop speech input' : 'Start speech input'}
                sx={{
                  color: props.isListening ? '#ef5350' : '#64748b',
                  '&:hover': {
                    backgroundColor: props.isListening ? '#ffebee' : 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                <MicIcon fontSize="small" />
              </IconButton>
            )}
          />
        </Box>
      </Box>

      {/* Send Button */}
      <Tooltip title={inputDisabled ? 'Cannot send in AI mode' : 'Send message'}>
        <span>
          <Box
            component="button"
            onClick={handleSend}
            disabled={inputDisabled || !localInputText.trim() || isSending}
            sx={getSendButtonStyle()}
          >
            {isSending ? (
              <>
                <CircularProgress size={16} sx={{ color: 'inherit' }} />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <SendIcon sx={{ fontSize: 18 }} />
                <span>Send</span>
              </>
            )}
          </Box>
        </span>
      </Tooltip>

      {/* Error Display */}
      {sendError && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            mb: 1,
            p: 1,
            backgroundColor: '#ffebee',
            borderRadius: '8px',
            border: '1px solid #ef5350'
          }}
        >
          <Typography variant="caption" color="error">
            {sendError}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

InputBar.displayName = 'InputBar';

export default InputBar;